import React, { useImperativeHandle, forwardRef, useRef } from 'react';
import { useRefresh } from 'react-admin';

import { useSnackbar } from 'notistack';

import { DialogoConfirmacao, DialogoJustificativa } from '../../../Components';
import { api, getListAllAPI } from '../../../services';
import {
  formatDateDDMMYYYYHHmm,
  decidirAcaoFiscal,
  reativacaoBloqueada,
  minutosRestantesDoPrazo,
  formatarDuracao,
  prazoDeCancelamentoEmMinutos,
  rotuloDaNota,
  mensagemNotaEmProcessamento,
  ACAO_FISCAL_CANCELAR,
  ACAO_FISCAL_INUTILIZAR,
  ACAO_FISCAL_BLOQUEAR,
  MENSAGEM_REATIVACAO_BLOQUEADA,
  OPCOES_JUSTIFICATIVA_CANCELAMENTO,
  JUSTIFICATIVA_TAMANHO_MINIMO,
  JUSTIFICATIVA_TAMANHO_MAXIMO,
} from '../../../utils';

/*
 * Cancelar uma venda também regulariza a nota fiscal na SEFAZ: evento de
 * cancelamento se ela está autorizada, inutilização da numeração se foi
 * rejeitada. Os dois atos exigem uma justificativa de no mínimo 15 caracteres,
 * então esses casos abrem o DialogoJustificativa; os demais seguem no
 * DialogoConfirmacao de sempre.
 */
const FormDialog = forwardRef(({ campoAtivo }, ref) => {
  const refresh = useRefresh();
  const { enqueueSnackbar } = useSnackbar();
  const refDialogoConfirmacao = useRef(null);
  const refDialogoJustificativa = useRef(null);

  function mensagemDoCancelamentoFiscal(venda, acaoFiscal) {
    const rotulo = rotuloDaNota(venda.tipoNota);

    if (acaoFiscal === ACAO_FISCAL_INUTILIZAR) {
      return `A ${rotulo} desta venda foi rejeitada. O cancelamento vai inutilizar na SEFAZ a numeração ${venda.numero_nfce} / série ${venda.serie_nfce}.`;
    }

    const restantes = minutosRestantesDoPrazo(venda);
    const prazo = formatarDuracao(prazoDeCancelamentoEmMinutos(venda.tipoNota));

    const aviso =
      restantes !== null && restantes > 0
        ? `Restam ${formatarDuracao(restantes)} do prazo de ${prazo}.`
        : `Atenção: o prazo de ${prazo} para cancelamento pode já ter expirado.`;

    return `Esta venda possui ${rotulo} autorizada — o cancelamento também cancelará a nota na SEFAZ. ${aviso}`;
  }

  async function getDataResource(record) {
    try {
      const data = await getListAllAPI(
        'vendas',
        ['id', 'asc'],
        { id: [record.id] },
        ['devolucoes'],
      );

      if (data.data.length === 0) throw 'Esta venda não existe!';

      const venda = data.data[0];
      const descricaoVenda = `nº ${venda.numero} realizada em ${formatDateDDMMYYYYHHmm(venda.data)}`;

      if (venda.cancelado) {
        if (reativacaoBloqueada(venda)) throw MENSAGEM_REATIVACAO_BLOQUEADA;

        refDialogoConfirmacao.current.handleOpen(
          'Reativação de Venda',
          `Tem certeza que deseja reativar a venda ${descricaoVenda} ?`,
          { ...record, statusCancelado: true },
        );
        return;
      }

      if (venda.devolucoes.length > 0) {
        throw 'Esta venda não pode ser cancelada, pois possui devoluções registradas nela!';
      }

      const acaoFiscal = decidirAcaoFiscal(venda);

      if (acaoFiscal === ACAO_FISCAL_BLOQUEAR) {
        throw mensagemNotaEmProcessamento(venda);
      }

      const codigo = { ...record, statusCancelado: false };

      if (
        acaoFiscal === ACAO_FISCAL_CANCELAR ||
        acaoFiscal === ACAO_FISCAL_INUTILIZAR
      ) {
        refDialogoJustificativa.current.handleOpen(
          'Cancelamento de Venda e Nota Fiscal',
          `${mensagemDoCancelamentoFiscal(venda, acaoFiscal)} Confirma o cancelamento da venda ${descricaoVenda}?`,
          codigo,
        );
        return;
      }

      refDialogoConfirmacao.current.handleOpen(
        'Cancelamento de Venda',
        `Tem certeza que deseja cancelar a venda ${descricaoVenda} ?`,
        codigo,
      );
    } catch (erros) {
      enqueueSnackbar(`${erros}`, {
        variant: 'error',
      });
    }
  }

  useImperativeHandle(ref, () => ({
    handleOpen(record) {
      getDataResource(record);
    },
  }));

  async function alterarStatus(record, justificativa) {
    const response = await api.put(`/vendas/${record.id}`, {
      status: !record.statusCancelado,
      justificativa,
    });
    const { status, message } = response.data;

    if (status) {
      enqueueSnackbar(
        record.statusCancelado
          ? 'Venda reativada com sucesso!'
          : 'Venda cancelada com sucesso!',
        { variant: 'success' },
      );
    } else {
      enqueueSnackbar(`${message}`, {
        variant: 'error',
      });
    }
    refresh();
  }

  return (
    <>
      <DialogoConfirmacao
        ref={refDialogoConfirmacao}
        handleConfirma={(record) => alterarStatus(record)}
      />
      <DialogoJustificativa
        ref={refDialogoJustificativa}
        opcoes={OPCOES_JUSTIFICATIVA_CANCELAMENTO}
        tamanhoMinimo={JUSTIFICATIVA_TAMANHO_MINIMO}
        tamanhoMaximo={JUSTIFICATIVA_TAMANHO_MAXIMO}
        rotuloCampo="Justificativa do cancelamento"
        handleConfirma={(record, justificativa) =>
          alterarStatus(record, justificativa)
        }
      />
    </>
  );
});

export default FormDialog;
